import {
  Component,
  Show,
  For,
  createSignal,
  createMemo,
  createEffect,
  createResource,
  onCleanup,
  untrack,
} from "solid-js";
import { useParams, useNavigate, useSearchParams } from "@solidjs/router";
import {
  Spinner,
  EmptyState,
  Button,
  Input,
  Select,
  Badge,
  UserPicker,
  ScrollToTopButton,
} from "../components/ui";
import type { SelectOption } from "../components/ui";
import { useWorkItem, useToast, useChildBreakdown } from "../hooks";
import {
  api,
  ApiError,
  enrichWorkItem,
  getProject,
  listLinkedDocuments,
  listMembers,
} from "../api";
import { recordVisit } from "../stores/recentsStore";
import { fetchAITeaser } from "../api/workItems";
import { surfaceQuotaExhausted } from "../utils/aiError";
import type {
  AiServed,
  Priority,
  QuotaScope,
  WorkItemAIContext,
  WorkItemTeaserResponse,
  WorkItem,
  CreateWorkItemRequest,
  LinkedDocument,
  ProjectMember,
} from "../api";
import { statusCategoryToVariant } from "../utils/status";
import { getDisplayId } from "../utils/displayId";
import { useProjectId, useProjectPrefix } from "../contexts/ProjectContext";
import {
  AutoCreateChildrenButton,
  BreakDownWithPlanAction,
  BusinessContextSection,
  ContextStalenessBanner,
  EstimateField,
  ParentBreadcrumb,
  PriorityMeter,
  ProposedChildrenPanel,
  ShippedEvidenceCard,
  TypeIcon,
} from "../components/work-items";
import { ProposalDiff } from "../components/proposals";
import { DegradedOutputBadge } from "../components/quota/DegradedOutputBadge";
import { parseChecklist } from "../utils/checklist";
import { LinksSection } from "../components/work-items/LinksSection";
import { DecisionsSection } from "../components/work-items/DecisionsSection";
import { ActivityThread } from "../components/work-items/activity/ActivityThread";
import { useChatCommand } from "../contexts/ChatCommandContext";

/**
 * Field label mapping for type_context fields
 */
const TYPE_CONTEXT_LABELS: Record<string, string> = {
  steps_to_reproduce: "Steps to Reproduce",
  expected_actual: "Expected vs Actual",
  implementation_notes: "Implementation Notes",
  acceptance_criteria: "Acceptance Criteria",
  scope_notes: "Scope Notes",
};

/**
 * Formats a field name into a human-readable label
 */
const formatFieldLabel = (fieldName: string): string => {
  return (
    TYPE_CONTEXT_LABELS[fieldName] ??
    fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};

/**
 * Priority options for the selector
 */
const priorityOptions: SelectOption[] = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "none", label: "None" },
];

/**
 * ItemDetailPage - Work item detail view
 *
 * Features:
 * - Two-column layout: main content (title, description) + sidebar (fields)
 * - Editable fields with auto-save (debounced for text, immediate for selectors)
 * - Loading and error states
 * - Back navigation
 */
export const ItemDetailPage: Component = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams<{ from?: string }>();
  const projectId = useProjectId();
  const prefix = useProjectPrefix();

  const { showError, showSuccess, showWarning } = useToast();

  // Scroll container — watched by the "go to top" FAB (FLOW-642)
  const [contentRef, setContentRef] = createSignal<HTMLDivElement>();

  const [isDeleting, setIsDeleting] = createSignal(false);

  const [isEnriching, setIsEnriching] = createSignal(false);
  const [aiContext, setAiContext] = createSignal<WorkItemAIContext | null>(
    null,
  );
  const [aiContextCached, setAiContextCached] = createSignal(false);
  // Which ceiling bound the analysis to the fast tier, when one did (FLOW-930).
  // Not persisted on `ai_context`, so it only lives as long as this response.
  const [aiContextScope, setAiContextScope] = createSignal<
    QuotaScope | undefined
  >();
  const [selectedSuggestions, setSelectedSuggestions] = createSignal<
    Set<number>
  >(new Set());
  const [dismissedPriority, setDismissedPriority] = createSignal<string | null>(
    null,
  );

  // FLOW-444 — auto-loaded Haiku teaser (one-line summary + complexity badge)
  const [teaser, setTeaser] =
    createSignal<AiServed<WorkItemTeaserResponse> | null>(null);
  const [isLoadingTeaser, setIsLoadingTeaser] = createSignal(false);
  // FLOW-838 — the work item id the auto-teaser effect has already tried.
  const [teaserAttemptedFor, setTeaserAttemptedFor] = createSignal<
    string | null
  >(null);

  const [localTitle, setLocalTitle] = createSignal("");
  const [localDescription, setLocalDescription] = createSignal("");
  const [isTitleDirty, setIsTitleDirty] = createSignal(false);
  const [isDescriptionDirty, setIsDescriptionDirty] = createSignal(false);

  let titleDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let descriptionDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  const {
    item,
    statuses,
    types,
    potentialParents,
    children,
    ancestors,
    loading,
    error,
    update,
    refetch,
    refetchAll,
  } = useWorkItem(() => params.id, projectId);

  // FLOW-909: the estimate unit is the project's, so it is fetched rather than
  // read off ProjectContext — which carries only the selected project's id,
  // and would also go stale after a settings save.
  //
  // Keyed on the *item's* project, not the selection. `useWorkItem` hits the
  // project-agnostic GET /work-items/{id}, so a deep link opened while another
  // project is selected (or a project switch mid-page) leaves the two
  // diverging — the same state the recordVisit effect below guards against.
  // Taking the selection's unit there would label the estimate wrongly and
  // make `onSave` send a unit the API rejects with 400. Falls back to the
  // selection only until the item arrives, so the field is not held back by a
  // second round trip on the common path where the two agree.
  //
  // Read only via `state === "ready"`: reading an errored resource throws, and
  // the nearest ErrorBoundary is the app root, so a failed project fetch must
  // cost the estimate field rather than the whole detail page.
  const [project] = createResource(
    () => item()?.project_id ?? projectId() ?? undefined,
    getProject,
  );

  const [showAddChild, setShowAddChild] = createSignal(false);
  const [childTitle, setChildTitle] = createSignal("");
  const [addingChild, setAddingChild] = createSignal(false);

  // Default child type for both manual and auto-created children: "Task",
  // falling back to the first type the project has.
  const childTypeId = createMemo(() => {
    const typesList = types();
    const taskType = typesList?.find((t) => t.name.toLowerCase() === "task");
    return taskType?.id ?? typesList?.[0]?.id;
  });

  // FLOW-747 — AI-proposed children the user can prune and create in bulk
  const childBreakdown = useChildBreakdown({
    workItemId: () => params.id,
    projectId,
    childTypeId,
    onCreated: refetchAll,
  });

  const [members, setMembers] = createSignal<ProjectMember[]>([]);
  const [membersLoading, setMembersLoading] = createSignal(false);
  const [assigneeUpdating, setAssigneeUpdating] = createSignal(false);

  const loadMembers = async () => {
    const pid = projectId();
    if (!pid) return;
    setMembersLoading(true);
    try {
      const res = await listMembers(pid);
      setMembers(res.members);
    } catch {
      // Silently fail — assignee picker shows empty options
    } finally {
      setMembersLoading(false);
    }
  };

  const [linkedDocs, setLinkedDocs] = createSignal<LinkedDocument[]>([]);

  const loadLinkedDocs = async () => {
    const pid = projectId();
    const workItem = item();
    if (!pid || !workItem) return;
    try {
      const docs = await listLinkedDocuments(pid, workItem.id);
      setLinkedDocs(docs);
    } catch {
      // Silently fail — section just shows empty state
    }
  };

  createEffect(() => {
    if (item()) {
      loadLinkedDocs();
    }
  });

  createEffect(() => {
    if (projectId()) {
      loadMembers();
    }
  });

  // FLOW-800 — feed the command palette's recently-visited list. Guarded on
  // the item's own project_id, not just the selected project: useWorkItem
  // hits the project-agnostic GET /work-items/{id}, so a deep link opened
  // while a different project is selected (or a project switch mid-page)
  // would otherwise record the visit under the wrong project (FLOW-800
  // security review).
  createEffect(() => {
    const workItem = item();
    const pid = projectId();
    if (workItem && pid && workItem.project_id === pid) {
      recordVisit("work_item", workItem.id, pid);
    }
  });

  createEffect(() => {
    const workItem = item();
    if (workItem && !isTitleDirty()) {
      setLocalTitle(workItem.title);
    }
    if (workItem && !isDescriptionDirty()) {
      setLocalDescription(workItem.description ?? "");
    }
  });

  onCleanup(() => {
    if (titleDebounceTimer) clearTimeout(titleDebounceTimer);
    if (descriptionDebounceTimer) clearTimeout(descriptionDebounceTimer);
  });

  // Handle save with error handling and 409 conflict detection
  const handleUpdate = async (patch: Parameters<typeof update>[0]) => {
    try {
      await update(patch);
      showSuccess("Saved");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        showWarning(
          "This item was modified by another user. Your changes were not saved. Reloading latest version.",
        );
        // Cancel pending debounce timers to prevent stale re-sends
        if (titleDebounceTimer) {
          clearTimeout(titleDebounceTimer);
          titleDebounceTimer = null;
        }
        if (descriptionDebounceTimer) {
          clearTimeout(descriptionDebounceTimer);
          descriptionDebounceTimer = null;
        }
        // Clear dirty flags so local state syncs with refetched data
        setIsTitleDirty(false);
        setIsDescriptionDirty(false);
        refetch();
      } else {
        showError(
          `Failed to save: ${e instanceof Error ? e.message : "Unknown error"}`,
        );
      }
    }
  };

  const handleTitleChange = (value: string) => {
    setLocalTitle(value);
    setIsTitleDirty(true);

    if (titleDebounceTimer) clearTimeout(titleDebounceTimer);
    titleDebounceTimer = setTimeout(() => {
      handleUpdate({ title: value });
      setIsTitleDirty(false);
    }, 500);
  };

  const handleDescriptionChange = (value: string) => {
    setLocalDescription(value);
    setIsDescriptionDirty(true);

    if (descriptionDebounceTimer) clearTimeout(descriptionDebounceTimer);
    descriptionDebounceTimer = setTimeout(() => {
      handleUpdate({ description: value });
      setIsDescriptionDirty(false);
    }, 500);
  };

  // Immediate updates for selectors
  const handleStatusChange = (statusId: string) => {
    handleUpdate({ status_id: statusId });
  };

  const handlePriorityChange = (priority: string) => {
    handleUpdate({ priority: priority as Priority });
  };

  const handleParentChange = (parentId: string) => {
    // Empty string means remove parent - backend uses remove_parent flag
    if (parentId) {
      handleUpdate({ parent_id: parentId });
    } else {
      handleUpdate({ remove_parent: true });
    }
  };

  const handleAssigneeChange = async (userId: string | null) => {
    setAssigneeUpdating(true);
    try {
      if (userId) {
        await handleUpdate({ assignee_id: userId });
      } else {
        await handleUpdate({ remove_assignee: true });
      }
    } finally {
      setAssigneeUpdating(false);
    }
  };

  const VALID_ORIGINS: Record<string, string> = {
    backlog: "/backlog",
    board: "/board",
  };
  const handleBack = () => {
    const origin = searchParams.from;
    navigate(
      origin && VALID_ORIGINS[origin] ? VALID_ORIGINS[origin] : "/board",
    );
  };

  const handleDelete = async () => {
    const workItem = item();
    if (!workItem) return;

    const confirmed = window.confirm(
      `Delete "${workItem.title}"? This cannot be undone.`,
    );
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await api.delete(`/work-items/${workItem.id}`);
      showSuccess("Item deleted");
      navigate("/board");
    } catch (e) {
      showError(
        `Failed to delete: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
      setIsDeleting(false);
    }
  };

  const handleEnrich = async (force = false) => {
    const workItem = item();
    if (!workItem || isEnriching()) return;

    setIsEnriching(true);
    try {
      const response = await enrichWorkItem(workItem.id, { force });
      setAiContext(response.context);
      setAiContextCached(response.cached);
      setAiContextScope(response.degradedScope);
      setSelectedSuggestions(new Set<number>());
      setDismissedPriority(null);
      if (response.cached) {
        showSuccess("Loaded cached analysis");
      } else {
        showSuccess("AI analysis complete");
      }
    } catch (e) {
      if (!surfaceQuotaExhausted(e))
        showError(
          `AI analysis failed: ${e instanceof Error ? e.message : "Service unavailable"}`,
        );
    } finally {
      setIsEnriching(false);
    }
  };

  // FLOW-444 — kick off teaser fetch when we have a work item but no full
  // AI Analysis yet. The backend handles read-or-generate and TTL.
  //
  // FLOW-838 — the guard is an untracked attempt marker, not a read of the
  // signals this effect writes. Tracking `isLoadingTeaser()` or `teaser()`
  // made the effect re-enter on its own `.finally()`; on the failure path
  // `teaser()` stays null, so nothing terminated the cycle and one open page
  // issued ~130 POST /ai-teaser per second. A failed attempt is now not
  // retried for this item without a fresh trigger.
  createEffect(() => {
    const wi = item();
    if (!wi) return;
    if (wi.ai_context) return; // full analysis wins; teaser is hidden
    if (untrack(teaserAttemptedFor) === wi.id) return; // one attempt per item
    setTeaserAttemptedFor(wi.id);
    setTeaser(null); // drop the previous item's teaser while this one loads
    setIsLoadingTeaser(true);
    fetchAITeaser(wi.id)
      .then((t) => setTeaser(t))
      .catch(() => {
        // silent fallback per spec — degrade to today's empty state
      })
      .finally(() => setIsLoadingTeaser(false));
  });

  // FLOW-794 — ask for the teaser again on the standard model
  const regenerateTeaser = () => {
    const wi = item();
    if (!wi || isLoadingTeaser()) return;
    setIsLoadingTeaser(true);
    fetchAITeaser(wi.id, true)
      .then((t) => setTeaser(t))
      .catch(() => {
        // silent fallback per spec — the current teaser stays on screen
      })
      .finally(() => setIsLoadingTeaser(false));
  };

  const { requestChat } = useChatCommand();

  const handleSendSuggestionsToChat = () => {
    const ctx = aiContext();
    const selected = selectedSuggestions();
    if (!ctx || selected.size === 0) return;

    const suggestions = ctx.suggestions
      .filter((_, i) => selected.has(i))
      .map((s) => `- ${s}`)
      .join("\n");

    const workItem = item();
    const title = workItem?.title ?? "this work item";

    requestChat(
      `Based on the AI analysis of "${title}", please help me implement these suggestions:\n\n${suggestions}`,
    );

    setSelectedSuggestions(new Set<number>());
  };

  const handleRefineSuggestionsInChat = () => {
    const ctx = aiContext();
    if (!ctx || ctx.suggestions.length === 0) return;

    const numbered = ctx.suggestions.map((s, i) => `${i + 1}. ${s}`).join("\n");

    const workItem = item();
    const title = workItem?.title ?? "this work item";

    requestChat(
      `I'd like to refine the AI-generated suggestions for "${title}". Here are the current suggestions:\n\n${numbered}\n\nAsk me which suggestions I'd like to drop, rewrite, or add. Once I tell you, produce a revised numbered list.`,
    );
  };

  const handleAddChild = async () => {
    const title = childTitle().trim();
    if (!title) return;

    const workItem = item();
    if (!workItem) return;

    const typeId = childTypeId();
    if (!typeId) return;

    setAddingChild(true);
    try {
      const payload: CreateWorkItemRequest = {
        title,
        item_type_id: typeId,
        parent_id: workItem.id,
      };
      await api.post<WorkItem>(
        `/projects/${workItem.project_id}/work-items`,
        payload,
      );
      setChildTitle("");
      setShowAddChild(false);
      showSuccess("Child item created");
      refetchAll();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to create child item");
    } finally {
      setAddingChild(false);
    }
  };

  const statusOptions = createMemo<SelectOption[]>(() => {
    const s = statuses();
    if (!s) return [];
    return s.map((status) => ({
      value: status.id,
      label: status.name,
    }));
  });

  const parentOptions = createMemo<SelectOption[]>(() => {
    const parents = potentialParents();
    if (!parents) return [];
    const options: SelectOption[] = [
      { value: "", label: "No parent" },
      ...parents.map((p) => ({
        value: p.id,
        label: `${getDisplayId(prefix(), p.id, p.item_number)} - ${p.title}`,
      })),
    ];
    return options;
  });

  const itemType = createMemo(() => {
    const workItem = item();
    const typesList = types();
    if (!workItem || !typesList) return null;
    return typesList.find((t) => t.id === workItem.item_type_id);
  });

  const breadcrumbSegments = createMemo(() =>
    (ancestors() ?? []).map((a) => ({
      id: a.id,
      itemNumber: a.item_number,
      title: a.title,
    })),
  );

  return (
    <div class="relative h-full flex flex-col bg-paper">
      {/* Header */}
      <div class="flex items-center gap-4 px-6 py-3 border-b border-line bg-surface">
        <button
          type="button"
          onClick={handleBack}
          class="p-2 text-ink-3 hover:text-ink hover:bg-sunken rounded focus-ring"
          title="Back to board"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </button>

        <Show when={item()}>
          {(workItem) => (
            <div class="flex items-center gap-3 min-w-0">
              <ParentBreadcrumb
                segments={breadcrumbSegments()}
                prefix={prefix()}
              />
              <Show when={breadcrumbSegments().length > 0}>
                <span aria-hidden="true" class="font-mono text-line-strong">
                  ›
                </span>
              </Show>
              <span
                data-display-id={getDisplayId(
                  prefix(),
                  workItem().id,
                  workItem().item_number,
                )}
                class="font-mono text-xs tabular-nums text-ink whitespace-nowrap font-semibold"
              >
                {getDisplayId(prefix(), workItem().id, workItem().item_number)}
              </span>
              <Show when={itemType()}>
                {(type) => (
                  <span class="inline-flex items-center gap-1 text-xs text-ink-2">
                    <TypeIcon typeName={type().name} />
                    {type().name}
                  </span>
                )}
              </Show>
              <Show when={workItem().is_blocked}>
                <span
                  title="Blocked by another item"
                  aria-label="Blocked by another item"
                >
                  <Badge variant="critical">Blocked</Badge>
                </span>
              </Show>
            </div>
          )}
        </Show>
      </div>

      {/* Content */}
      <div ref={setContentRef} class="flex-1 overflow-auto">
        <Show
          when={!loading()}
          fallback={
            <div class="h-full flex items-center justify-center">
              <div class="flex flex-col items-center">
                <Spinner size="lg" />
                <p class="mt-4 text-sm text-ink-3">Loading work item...</p>
              </div>
            </div>
          }
        >
          <Show
            when={!error()}
            fallback={
              <div class="h-full flex items-center justify-center p-8">
                <EmptyState
                  icon={
                    <svg
                      class="w-12 h-12"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.5"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  }
                  title="Error loading work item"
                  description={
                    error()?.message ?? "An unexpected error occurred"
                  }
                  action={
                    <Button variant="primary" size="sm" onClick={handleBack}>
                      Back to board
                    </Button>
                  }
                />
              </div>
            }
          >
            <Show
              when={item()}
              fallback={
                <div class="h-full flex items-center justify-center p-8">
                  <EmptyState
                    icon={
                      <svg
                        class="w-12 h-12"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.5"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    }
                    title="Work item not found"
                    description="The requested work item does not exist or has been deleted."
                    action={
                      <Button variant="primary" size="sm" onClick={handleBack}>
                        Back to board
                      </Button>
                    }
                  />
                </div>
              }
            >
              {(workItem) => (
                <div class="max-w-6xl mx-auto p-6">
                  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content (2/3 width on large screens) */}
                    <div class="lg:col-span-2 space-y-6">
                      <ContextStalenessBanner workItemId={workItem().id} />
                      <BusinessContextSection workItemId={workItem().id} />

                      {/* Title */}
                      <div class="bg-surface rounded border border-line p-4">
                        <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-2">
                          Title
                        </label>
                        <input
                          type="text"
                          value={localTitle()}
                          onInput={(e) =>
                            handleTitleChange(e.currentTarget.value)
                          }
                          class="w-full text-lg font-semibold text-ink placeholder:text-ink-3 border-0 border-b border-transparent focus:border-ink focus:outline-none bg-transparent p-0"
                          placeholder="Enter title..."
                        />
                      </div>

                      {/* Description */}
                      <div class="bg-surface rounded border border-line p-4">
                        <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-2">
                          Description
                        </label>
                        <textarea
                          value={localDescription()}
                          onInput={(e) =>
                            handleDescriptionChange(e.currentTarget.value)
                          }
                          rows={8}
                          class="w-full text-sm text-ink border border-line rounded p-3 resize-none focus:border-ink"
                          placeholder="Enter description..."
                        />
                      </div>

                      {/* Type-Specific Context Fields (read-only) */}
                      <Show
                        when={(() => {
                          const typeContext = item()?.type_context;
                          return typeContext &&
                            Object.keys(typeContext).length > 0
                            ? typeContext
                            : null;
                        })()}
                      >
                        {(typeContext) => (
                          <div class="bg-surface rounded border border-line p-4">
                            <h3 class="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-4">
                              {itemType()?.name} Details
                            </h3>
                            <div class="space-y-4">
                              <For each={Object.entries(typeContext())}>
                                {([fieldName, fieldValue]) => {
                                  const checklist = () =>
                                    fieldName === "acceptance_criteria" &&
                                    fieldValue
                                      ? parseChecklist(fieldValue)
                                      : null;
                                  return (
                                    <div>
                                      <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-2">
                                        {formatFieldLabel(fieldName)}
                                      </label>
                                      <Show
                                        when={checklist()}
                                        fallback={
                                          <div class="text-sm text-ink whitespace-pre-wrap bg-paper rounded p-3 border border-line font-mono text-xs">
                                            <Show
                                              when={fieldValue}
                                              fallback={
                                                <span class="text-ink-3 italic">
                                                  Not provided
                                                </span>
                                              }
                                            >
                                              {fieldValue}
                                            </Show>
                                          </div>
                                        }
                                      >
                                        {(items) => (
                                          <ul
                                            role="list"
                                            class="font-mono text-xs leading-5 bg-paper rounded p-3 border border-line"
                                          >
                                            <For each={items()}>
                                              {(it) => (
                                                <li
                                                  class={
                                                    "flex gap-2 " +
                                                    (it.checked
                                                      ? "text-ink-3"
                                                      : "text-ink")
                                                  }
                                                >
                                                  <span class="shrink-0 select-none">
                                                    {it.checked ? "[x]" : "[ ]"}
                                                  </span>
                                                  <span class="min-w-0">
                                                    {it.text}
                                                  </span>
                                                </li>
                                              )}
                                            </For>
                                          </ul>
                                        )}
                                      </Show>
                                    </div>
                                  );
                                }}
                              </For>
                            </div>
                          </div>
                        )}
                      </Show>

                      {/* Child Items Section */}
                      <Show when={workItem().depth < 3}>
                        <div class="bg-surface rounded border border-line p-4">
                          <div class="flex items-center justify-between mb-3">
                            <div class="flex items-center gap-2">
                              <h3 class="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3">
                                Child Items
                              </h3>
                              <Show
                                when={
                                  children() && (children() ?? []).length > 0
                                }
                              >
                                <span class="font-mono text-2xs tabular-nums text-ink-3">
                                  {(children() ?? []).length}
                                </span>
                              </Show>
                            </div>
                            <div class="flex items-center gap-1">
                              <Show when={itemType()?.name === "Epic"}>
                                <BreakDownWithPlanAction
                                  epicId={workItem().id}
                                />
                              </Show>
                              <AutoCreateChildrenButton
                                breakdown={childBreakdown}
                              />
                              <Show when={!showAddChild()}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowAddChild(true)}
                                >
                                  + Add Child
                                </Button>
                              </Show>
                            </div>
                          </div>

                          <ProposedChildrenPanel breakdown={childBreakdown} />

                          {/* Inline add child form */}
                          <Show when={showAddChild()}>
                            <form
                              class="flex items-start gap-2 mb-3"
                              onSubmit={(e) => {
                                e.preventDefault();
                                handleAddChild();
                              }}
                            >
                              <div class="flex-1">
                                <Input
                                  placeholder="Child item title..."
                                  value={childTitle()}
                                  onInput={setChildTitle}
                                  maxLength={500}
                                  autofocus
                                />
                              </div>
                              <div class="flex items-center gap-1.5 pt-px">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  type="submit"
                                  disabled={
                                    !childTitle().trim() || addingChild()
                                  }
                                  loading={addingChild()}
                                >
                                  Add
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setShowAddChild(false);
                                    setChildTitle("");
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </form>
                          </Show>

                          <Show
                            when={children() && (children() ?? []).length > 0}
                            fallback={
                              <div class="flex flex-col items-center py-6 text-center">
                                <svg
                                  class="w-8 h-8 text-line-strong mb-2"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="1.5"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M4 6h16M4 12h8m-8 6h16"
                                  />
                                </svg>
                                <p class="text-sm text-ink-3">
                                  No child items yet
                                </p>
                              </div>
                            }
                          >
                            <div class="divide-y divide-line">
                              <For each={children()}>
                                {(child) => {
                                  const childType = () =>
                                    types()?.find(
                                      (t) => t.id === child.item_type_id,
                                    );
                                  const childStatus = () =>
                                    statuses()?.find(
                                      (s) => s.id === child.status_id,
                                    );
                                  const childStatusVariant = () => {
                                    const status = childStatus();
                                    return status
                                      ? statusCategoryToVariant[status.category]
                                      : "default";
                                  };

                                  return (
                                    <div
                                      class="flex items-center gap-3 py-2 px-2 cursor-pointer hover:bg-paper rounded transition-colors"
                                      onClick={() =>
                                        navigate(`/items/${child.id}`)
                                      }
                                    >
                                      <TypeIcon
                                        typeName={childType()?.name ?? "Task"}
                                      />
                                      <span class="sr-only">
                                        {childType()?.name}
                                      </span>
                                      <span class="font-mono text-xs tabular-nums text-ink-2 whitespace-nowrap">
                                        {getDisplayId(
                                          prefix(),
                                          child.id,
                                          child.item_number,
                                        )}
                                      </span>
                                      <Badge
                                        variant={childStatusVariant()}
                                        class="text-2xs px-1.5"
                                      >
                                        {childStatus()?.name ?? "Unknown"}
                                      </Badge>
                                      <span class="text-sm text-ink truncate flex-1 min-w-0">
                                        {child.title}
                                      </span>
                                      <span class="w-5 flex justify-center">
                                        <PriorityMeter
                                          priority={child.priority}
                                        />
                                      </span>
                                    </div>
                                  );
                                }}
                              </For>
                            </div>
                          </Show>
                        </div>
                      </Show>

                      <ActivityThread workItemId={workItem().id} />

                      <LinksSection
                        workItemId={workItem().id}
                        projectId={projectId() ?? ""}
                        linkedDocuments={linkedDocs()}
                      />

                      <DecisionsSection
                        workItemId={workItem().id}
                        projectId={projectId() ?? ""}
                      />
                    </div>

                    {/* Sidebar (1/3 width on large screens) */}
                    <aside class="bg-surface rounded border border-line divide-y divide-line self-start">
                      {/* Status */}
                      <div class="p-4">
                        <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-2">
                          Status
                        </label>
                        <Select
                          options={statusOptions()}
                          value={workItem().status_id}
                          onChange={handleStatusChange}
                          renderOption={(option) => {
                            const status = statuses()?.find(
                              (s) => s.id === option.value,
                            );
                            const variant = status
                              ? statusCategoryToVariant[status.category]
                              : "default";
                            return (
                              <div class="flex items-center gap-2">
                                <Badge variant={variant}>{option.label}</Badge>
                              </div>
                            );
                          }}
                        />
                      </div>

                      {/* Priority */}
                      <div class="p-4">
                        <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-2">
                          Priority
                        </label>
                        <Select
                          options={priorityOptions}
                          value={workItem().priority}
                          onChange={handlePriorityChange}
                          renderOption={(option) => (
                            <div class="flex items-center gap-2">
                              <PriorityMeter
                                priority={option.value as Priority}
                              />
                              <span class="text-sm text-ink">
                                {option.label}
                              </span>
                            </div>
                          )}
                        />
                      </div>

                      {/* Estimate */}
                      <div class="p-4" data-estimate-field>
                        <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-2">
                          Estimate
                        </label>
                        <Show when={project.state === "ready" && project()}>
                          {(p) => (
                            <EstimateField
                              value={
                                workItem().estimate_value == null
                                  ? null
                                  : Number(workItem().estimate_value)
                              }
                              unit={p().estimate_unit}
                              onSave={(payload) => handleUpdate(payload)}
                              onClear={() =>
                                handleUpdate({ remove_estimate: true })
                              }
                            />
                          )}
                        </Show>
                      </div>

                      {/* Assignee */}
                      <div class="p-4">
                        <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-2">
                          Assignee
                        </label>
                        <Show
                          when={!membersLoading()}
                          fallback={
                            <p class="text-sm text-ink-3">Loading...</p>
                          }
                        >
                          {/* TODO(FLOW-341): restrict to non-viewer roles once role is available */}
                          <UserPicker
                            members={members()}
                            selectedUserId={workItem().assignee_id ?? null}
                            onSelect={handleAssigneeChange}
                            loading={assigneeUpdating()}
                          />
                        </Show>
                      </div>

                      {/* AI Analysis Panel */}
                      <div class="p-4 bg-paper" data-testid="ai-analysis-panel">
                        <div class="flex items-center justify-between mb-3">
                          <div class="flex items-center gap-2">
                            <svg
                              class="w-5 h-5 text-ink-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="1.8"
                                d="M9.5 4.5 11 8l3.5 1.5L11 11l-1.5 3.5L8 11l-3.5-1.5L8 8l1.5-3.5Zm8 8 1 2.4 2.4 1-2.4 1-1 2.4-1-2.4-2.4-1 2.4-1 1-2.4Zm-3-9 .8 1.8 1.8.7-1.8.7-.8 1.8-.8-1.8-1.8-.7 1.8-.7.8-1.8Z"
                              />
                            </svg>
                            <span class="font-mono text-[9px] uppercase tracking-[0.12em] px-1 rounded-sm border border-signal-magenta/25 bg-signal-magenta-tint text-signal-magenta">
                              ai
                            </span>
                            <h3 class="text-sm font-semibold text-ink">
                              AI Analysis
                            </h3>
                            <Show when={aiContext()?.degraded === true}>
                              <DegradedOutputBadge
                                onRegenerate={() => void handleEnrich(true)}
                                regenerating={isEnriching()}
                                scope={aiContextScope()}
                              />
                            </Show>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEnrich(!!aiContext())}
                            loading={isEnriching()}
                          >
                            {aiContext() ? "Re-analyze" : "Analyze"}
                          </Button>
                        </div>

                        <Show
                          when={!isEnriching()}
                          fallback={
                            <div class="flex flex-col items-center py-4">
                              <Spinner size="sm" />
                              <p class="mt-2 text-xs text-ink-3">
                                Analyzing work item...
                              </p>
                            </div>
                          }
                        >
                          <Show
                            when={aiContext()}
                            fallback={
                              <Show
                                when={teaser()}
                                fallback={
                                  <Show
                                    when={isLoadingTeaser()}
                                    fallback={
                                      <p class="text-sm text-ink-2">
                                        Click "Analyze" to get AI-powered
                                        insights about this work item.
                                      </p>
                                    }
                                  >
                                    <div class="flex items-center gap-2 py-1">
                                      <Spinner size="sm" />
                                      <span class="text-xs text-ink-3">
                                        Generating quick summary…
                                      </span>
                                    </div>
                                  </Show>
                                }
                              >
                                {(t) => (
                                  <div class="space-y-2">
                                    <p class="text-sm text-ink-2">
                                      {t().summary}
                                    </p>
                                    <div class="flex items-center gap-2">
                                      <Badge
                                        variant={
                                          t().complexity === "high"
                                            ? "critical"
                                            : t().complexity === "medium"
                                              ? "medium"
                                              : "default"
                                        }
                                      >
                                        Complexity: {t().complexity}
                                      </Badge>
                                      <Show when={t().degraded}>
                                        <DegradedOutputBadge
                                          onRegenerate={regenerateTeaser}
                                          regenerating={isLoadingTeaser()}
                                          scope={t().degradedScope}
                                        />
                                      </Show>
                                    </div>
                                  </div>
                                )}
                              </Show>
                            }
                          >
                            {(context) => (
                              <div class="space-y-3">
                                {/* Related Items */}
                                <Show when={context().related_items.length > 0}>
                                  <div>
                                    <h4 class="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-1">
                                      Related Items
                                    </h4>
                                    <ul class="space-y-1">
                                      <For each={context().related_items}>
                                        {(related) => (
                                          <li class="text-xs flex items-center gap-1">
                                            <span class="font-mono text-xs tabular-nums text-ink">
                                              {getDisplayId(
                                                prefix(),
                                                related.id,
                                              )}
                                            </span>
                                            <span class="text-ink-3">
                                              ({related.relationship})
                                            </span>
                                          </li>
                                        )}
                                      </For>
                                    </ul>
                                  </div>
                                </Show>

                                {/* Suggested Labels */}
                                <Show
                                  when={context().suggested_labels.length > 0}
                                >
                                  <div>
                                    <h4 class="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-1">
                                      Suggested Labels
                                    </h4>
                                    <div class="flex flex-wrap gap-1">
                                      <For each={context().suggested_labels}>
                                        {(label) => (
                                          <Badge variant="default">
                                            {label.name}
                                          </Badge>
                                        )}
                                      </For>
                                    </div>
                                  </div>
                                </Show>

                                {/* Suggested Priority */}
                                <Show when={context().suggested_priority}>
                                  {(priority) => (
                                    <div>
                                      <h4 class="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-1">
                                        Suggested Priority
                                      </h4>
                                      <Show
                                        when={item()?.priority !== priority()}
                                        fallback={
                                          <svg
                                            class="w-4 h-4 text-signal-green"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            aria-label={`Priority already ${priority()}`}
                                            role="img"
                                          >
                                            <path
                                              fill-rule="evenodd"
                                              d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                              clip-rule="evenodd"
                                            />
                                          </svg>
                                        }
                                      >
                                        <Show
                                          when={
                                            dismissedPriority() !== priority()
                                          }
                                        >
                                          <ProposalDiff
                                            before={`priority: ${item()?.priority}`}
                                            after={`priority: ${priority()}`}
                                            onAccept={() =>
                                              handleUpdate({
                                                priority:
                                                  priority() as Priority,
                                              })
                                            }
                                            onDismiss={() =>
                                              setDismissedPriority(priority())
                                            }
                                          />
                                        </Show>
                                      </Show>
                                    </div>
                                  )}
                                </Show>

                                {/* Complexity Assessment */}
                                <Show when={context().complexity_assessment}>
                                  {(assessment) => (
                                    <div>
                                      <h4 class="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-1">
                                        Complexity
                                      </h4>
                                      <p class="text-xs text-ink-2">
                                        {assessment()}
                                      </p>
                                    </div>
                                  )}
                                </Show>

                                {/* Suggestions */}
                                <Show when={context().suggestions.length > 0}>
                                  <div>
                                    <h4 class="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-1">
                                      Suggestions
                                    </h4>
                                    <ul class="space-y-1.5">
                                      <For each={context().suggestions}>
                                        {(suggestion, i) => (
                                          <li class="text-xs text-ink-2 flex items-start gap-2">
                                            <label class="flex h-6.5 w-6.5 shrink-0 -my-1.25 items-center justify-center cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={selectedSuggestions().has(
                                                  i(),
                                                )}
                                                aria-label={`Select suggestion: ${suggestion}`}
                                                onChange={() => {
                                                  setSelectedSuggestions(
                                                    (prev) => {
                                                      const next = new Set(
                                                        prev,
                                                      );
                                                      if (next.has(i())) {
                                                        next.delete(i());
                                                      } else {
                                                        next.add(i());
                                                      }
                                                      return next;
                                                    },
                                                  );
                                                }}
                                                class="h-3.5 w-3.5 rounded accent-ink"
                                              />
                                            </label>
                                            <span>{suggestion}</span>
                                          </li>
                                        )}
                                      </For>
                                    </ul>
                                    <Show when={selectedSuggestions().size > 0}>
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        class="mt-2 w-full"
                                        onClick={handleSendSuggestionsToChat}
                                        ariaLabel={`Apply ${selectedSuggestions().size} selected suggestion${selectedSuggestions().size !== 1 ? "s" : ""}`}
                                        leftIcon={
                                          <svg
                                            class="w-3.5 h-3.5"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            aria-hidden="true"
                                          >
                                            <path d="M3.105 2.288a.75.75 0 0 0-.826.95l1.414 4.926A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.897 28.897 0 0 0 15.293-7.155.75.75 0 0 0 0-1.114A28.897 28.897 0 0 0 3.105 2.288Z" />
                                          </svg>
                                        }
                                      >
                                        Apply Suggestion
                                        {selectedSuggestions().size !== 1
                                          ? "s"
                                          : ""}{" "}
                                        ({selectedSuggestions().size})
                                      </Button>
                                    </Show>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      class="mt-2 w-full"
                                      onClick={handleRefineSuggestionsInChat}
                                      ariaLabel="Refine these suggestions in chat"
                                      leftIcon={
                                        <svg
                                          class="w-3.5 h-3.5"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                          aria-hidden="true"
                                        >
                                          <path d="M3.43 2.524A41.29 41.29 0 0 1 10 2c2.236 0 4.43.18 6.57.524 1.437.231 2.43 1.49 2.43 2.902v5.148c0 1.413-.993 2.67-2.43 2.902a41.029 41.029 0 0 1-3.42.39c-.572.052-1.096.32-1.452.74L10 16.75l-1.698-2.144a2.305 2.305 0 0 0-1.452-.74 40.97 40.97 0 0 1-3.42-.39C1.993 13.245 1 11.986 1 10.574V5.426c0-1.413.993-2.67 2.43-2.902Z" />
                                        </svg>
                                      }
                                    >
                                      Refine in Chat
                                    </Button>
                                  </div>
                                </Show>

                                {/* Last analyzed timestamp */}
                                <div class="pt-2 border-t border-line">
                                  <p class="text-xs text-ink-3">
                                    {aiContextCached() ? "Cached • " : ""}
                                    Analyzed{" "}
                                    {new Date(
                                      context().generated_at,
                                    ).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            )}
                          </Show>
                        </Show>
                      </div>

                      {/* Hidden until the fetch finds evidence — an empty
                          section would still draw a rail divider. */}
                      <div class="p-4 empty:hidden">
                        <ShippedEvidenceCard workItemId={workItem().id} />
                      </div>

                      {/* Parent */}
                      <div class="p-4" data-parent-select>
                        <div class="flex items-center justify-between mb-2">
                          <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3">
                            Parent
                          </label>
                          <Show when={workItem().parent_id}>
                            {(parentId) => (
                              <button
                                type="button"
                                onClick={() => navigate(`/items/${parentId()}`)}
                                class="inline-flex items-center gap-1 h-7 px-2 -mx-2 text-xs font-medium text-ink-2 hover:text-ink underline-offset-2 hover:underline rounded-sm focus-ring"
                                aria-label="Open parent item"
                              >
                                Open
                                <svg
                                  class="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                                  />
                                </svg>
                              </button>
                            )}
                          </Show>
                        </div>
                        <Select
                          options={parentOptions()}
                          value={workItem().parent_id ?? ""}
                          onChange={handleParentChange}
                          searchable
                          placeholder="Select parent..."
                        />
                        <Show when={workItem().depth >= 3}>
                          <p class="mt-2 text-xs text-signal-amber">
                            This item is at maximum depth and cannot have
                            children.
                          </p>
                        </Show>
                      </div>

                      {/* Labels (read-only for MVP — only shown when present) */}
                      <Show
                        when={workItem().labels && workItem().labels.length > 0}
                      >
                        <div class="p-4">
                          <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-2">
                            Labels
                          </label>
                          <div class="flex flex-wrap gap-2">
                            <For each={workItem().labels}>
                              {(labelId) => (
                                <Badge variant="default">
                                  {labelId.slice(-6)}
                                </Badge>
                              )}
                            </For>
                          </div>
                        </div>
                      </Show>

                      {/* Metadata */}
                      <div class="p-4">
                        <label class="block font-mono text-[9px] uppercase tracking-[0.12em] text-ink-3 mb-2">
                          Details
                        </label>
                        <dl class="space-y-2 text-sm">
                          <div class="flex justify-between">
                            <dt class="text-ink-3">Created</dt>
                            <dd class="font-mono text-xs tabular-nums text-ink">
                              {new Date(
                                workItem().created_at,
                              ).toLocaleDateString()}
                            </dd>
                          </div>
                          <div class="flex justify-between">
                            <dt class="text-ink-3">Updated</dt>
                            <dd class="font-mono text-xs tabular-nums text-ink">
                              {new Date(
                                workItem().updated_at,
                              ).toLocaleDateString()}
                            </dd>
                          </div>
                          <Show when={workItem().started_at}>
                            {(startedAt) => (
                              <div class="flex justify-between">
                                <dt class="text-ink-3">Started</dt>
                                <dd class="font-mono text-xs tabular-nums text-ink">
                                  {new Date(startedAt()).toLocaleDateString()}
                                </dd>
                              </div>
                            )}
                          </Show>
                          <Show when={workItem().completed_at}>
                            {(completedAt) => (
                              <div class="flex justify-between">
                                <dt class="text-ink-3">Completed</dt>
                                <dd class="font-mono text-xs tabular-nums text-ink">
                                  {new Date(completedAt()).toLocaleDateString()}
                                </dd>
                              </div>
                            )}
                          </Show>
                          <div class="flex justify-between">
                            <dt class="text-ink-3">Hierarchy depth</dt>
                            <dd class="font-mono text-xs tabular-nums text-ink">
                              {workItem().depth}
                            </dd>
                          </div>
                        </dl>
                      </div>

                      {/* Delete (destructive action — placed last) */}
                      <div class="p-4">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleDelete}
                          loading={isDeleting()}
                          class="w-full"
                        >
                          Delete item
                        </Button>
                      </div>
                    </aside>
                  </div>
                </div>
              )}
            </Show>
          </Show>
        </Show>
      </div>

      <ScrollToTopButton container={contentRef()} />
    </div>
  );
};

export default ItemDetailPage;
